/**
 * Admin API client for Superblog JSON builder.
 * Base path: /admin/superblog
 */
import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

function getAuthHeaders(json = true) {
  const headers = {};
  const token = getBearerToken();
  if (token) headers.Authorization = token;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export function apiErrorMessage(body, fallback = 'Request failed') {
  const code = body?.error_code
    ?? body?.errorCode
    ?? body?.data?.error_code
    ?? body?.data?.errorCode
    ?? '';
  const raw = body?.message || body?.error || fallback;
  if (code === 'ERR_GENERATION_MISSING_NUMBERS') {
    const base = body?.message || 'Generated copy is missing the score numbers that were sent.';
    return String(base).includes(code) ? String(base) : `${base} (${code})`;
  }
  if (code && !String(raw).includes(String(code))) return `${raw} (${code})`;
  return raw;
}

async function parseError(res) {
  let msg = `Request failed: ${res.status}`;
  try {
    const body = await res.json();
    msg = apiErrorMessage(body, msg);
  } catch {
    try {
      const text = await res.text();
      if (text) msg = text;
    } catch {}
  }
  throw new Error(msg);
}

/** Envelope success is the string "10000" on status_code or statusCode. */
export function isSuperblogApiSuccess(res) {
  const code = res?.status_code ?? res?.statusCode;
  return String(code ?? '') === '10000';
}

/** Preview + build payload live in body.data, not body root */
export function unwrapApiPayload(body) {
  return body?.data ?? body;
}

/** Markdown fallback only. posts[0].content is HTML and is not this string. */
export function getPreviewFromPayload(payload) {
  if (!payload) return '';
  return (
    payload.markdownPreview
    ?? payload.markdown_preview
    ?? (typeof payload.preview === 'string' ? payload.preview : '')
    ?? ''
  );
}

/** Full HTML document for iframe srcDoc (do not wrap further) */
export function getHtmlPreviewFromPayload(payload) {
  if (!payload) return '';
  return (
    payload.htmlPreview
    ?? payload.html_preview
    ?? payload.previewHtmlDocument
    ?? ''
  );
}

/** GET /admin/superblog/source-types */
export async function fetchSourceTypes() {
  const res = await fetch(`${BASE_URL}/admin/superblog/source-types`, {
    credentials: 'include',
    headers: getAuthHeaders(false),
  });
  if (!res.ok) await parseError(res);
  const body = await res.json().catch(() => ({}));
  return body.data ?? body;
}

function listFromEnvelope(body) {
  const data = body?.data ?? body;
  return data?.items
    ?? data?.sources
    ?? data?.competitions
    ?? data?.matches
    ?? data?.posts
    ?? data?.articles
    ?? data?.results
    ?? data?.list
    ?? (Array.isArray(data) ? data : []);
}

/** GET /admin/superblog/competitions?q=&limit= */
export async function searchCompetitions({ q = '', limit = 40 } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (q) qs.set('q', q);
  const res = await fetch(`${BASE_URL}/admin/superblog/competitions?${qs}`, {
    credentials: 'include',
    headers: getAuthHeaders(false),
  });
  if (!res.ok) await parseError(res);
  const body = await res.json().catch(() => ({}));
  return listFromEnvelope(body);
}

/**
 * GET /admin/superblog/sources?type=&cid=&mid=&q=&limit=&offset=
 * Match lists take cid (the competition id).
 * Dugout post lists for one match take cid and mid together.
 */
export async function searchSources({ type, q = '', limit = 20, offset = 0, cid, mid, kind } = {}) {
  const hasCid = cid != null && cid !== '';
  const hasMid = mid != null && mid !== '';
  // Dugout lists are unscoped, or scoped with cid and mid together. One of the pair is a 400.
  if (type === 'dugout_posts' && hasCid !== hasMid) return [];
  const qs = new URLSearchParams({
    type: String(type),
    limit: String(limit),
    offset: String(offset),
  });
  if (q) qs.set('q', q);
  if (hasCid) qs.set('cid', String(cid));
  if (type === 'dugout_posts' && hasMid) qs.set('mid', String(mid));
  if (kind === 'image' || kind === 'text') qs.set('kind', kind);
  const res = await fetch(`${BASE_URL}/admin/superblog/sources?${qs}`, {
    credentials: 'include',
    headers: getAuthHeaders(false),
  });
  if (!res.ok) await parseError(res);
  const body = await res.json().catch(() => ({}));
  return listFromEnvelope(body);
}

/** POST /admin/superblog/build — returns { body, payload } where payload = body.data */
export async function buildSuperblogJson(payload) {
  const res = await fetch(`${BASE_URL}/admin/superblog/build`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) await parseError(res);
  const body = await res.json().catch(() => ({}));
  if (!isSuperblogApiSuccess(body)) {
    throw new Error(apiErrorMessage(body, 'Build failed'));
  }
  return { body, payload: unwrapApiPayload(body) };
}

/**
 * PATCH /admin/superblog/blogs/{id}
 * id is the integer posts[0].id from the build response, never the article slug.
 * Omitted keys stay as stored. Tone and writing notes re-generate that same blog.
 */
export async function patchSuperblogBlog(id, body) {
  const numericId = Number(id);
  if (!Number.isInteger(numericId) || numericId <= 0) {
    throw new Error('Blog id must be the numeric id from the generate response.');
  }
  const res = await fetch(`${BASE_URL}/admin/superblog/blogs/${numericId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) await parseError(res);
  const json = await res.json().catch(() => ({}));
  if (!isSuperblogApiSuccess(json)) {
    throw new Error(apiErrorMessage(json, 'Update failed'));
  }
  return { body: json, payload: unwrapApiPayload(json) };
}

/** POST /admin/superblog/upload-image — multipart field name image. Returns the HTTPS URL. */
export async function uploadSuperblogImage(file) {
  const form = new FormData();
  form.append('image', file);
  const res = await fetch(`${BASE_URL}/admin/superblog/upload-image`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(false),
    body: form,
  });
  if (!res.ok) await parseError(res);
  const body = await res.json().catch(() => ({}));
  if (!isSuperblogApiSuccess(body)) {
    throw new Error(apiErrorMessage(body, 'Upload failed'));
  }
  const payload = unwrapApiPayload(body);
  const url = payload?.imageUrl || payload?.image_url;
  if (!url) throw new Error('Upload did not return an image URL');
  return url;
}

/** Save data.posts from a build or patch response. Do not POST /build/download. */
export function downloadPostsJson(posts, filename = 'superblog.json') {
  const blob = new Blob([`${JSON.stringify(posts, null, 2)}\n`], { type: 'application/json' });
  triggerBlobDownload(blob, filename);
}

/**
 * POST /admin/superblog/build/preview — raw text/html (not wrapped in { data }).
 * Falls back to opening htmlPreview from a prior /build result via openHtmlPreviewInTab.
 */
export async function previewSuperblogHtml(payload) {
  const res = await fetch(`${BASE_URL}/admin/superblog/build/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) await parseError(res);
  return res.text();
}

export function openHtmlPreviewInTab(html) {
  if (!html) throw new Error('No HTML preview available');
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after the new tab has a chance to load
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
